import {
  Incident,
  IncidentList,
  ParticipantUser,
  ParticipantUserList,
  type IncidentNote,
  type IncidentQuery,
  type IncidentStatusChange,
  type IncidentSubmission,
  type ParticipantUserInvite,
  type ParticipantUserUpdate,
  type PasswordChange,
  Recall,
  RecallList,
  type AffectedUnitNote,
  type AffectedUnitStatusChange,
  type RecallClose,
  type RecallInput,
  type RecallNote,
  type RecallQuery,
} from '@regigov/contracts';
import { z } from 'zod';
import { api } from './client';

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const patch = (body: unknown): RequestInit => ({ method: 'PATCH', body: JSON.stringify(body) });

/** `participantId` is `me` for a participant's own users, or an id for CER. */
export const usersApi = {
  list: (participantId: string) => api(`/participants/${participantId}/users`, ParticipantUserList),
  invite: (participantId: string, invite: ParticipantUserInvite) => api(`/participants/${participantId}/users`, ParticipantUser, json(invite)),
  update: (participantId: string, id: string, update: ParticipantUserUpdate) => api(`/participants/${participantId}/users/${id}`, ParticipantUser, patch(update)),
  changePassword: (change: PasswordChange) => api('/auth/password', z.undefined(), json(change)),
};

export const incidentsApi = {
  list: (query: IncidentQuery = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
    const qs = params.toString();
    return api(`/incidents${qs ? `?${qs}` : ''}`, IncidentList);
  },
  get: (id: string) => api(`/incidents/${id}`, Incident),
  raise: (submission: IncidentSubmission) => api('/incidents', Incident, json(submission)),
  addNote: (id: string, note: IncidentNote) => api(`/incidents/${id}/notes`, Incident, json(note)),
  changeStatus: (id: string, change: IncidentStatusChange) => api(`/incidents/${id}/status`, Incident, json(change)),
};

export const recallsApi = {
  list: (query: RecallQuery = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
    const qs = params.toString();
    return api(`/recalls${qs ? `?${qs}` : ''}`, RecallList);
  },
  get: (id: string) => api(`/recalls/${id}`, Recall),
  raise: (input: RecallInput) => api('/recalls', Recall, json(input)),
  addNote: (id: string, note: RecallNote) => api(`/recalls/${id}/notes`, Recall, json(note)),
  refresh: (id: string) => api(`/recalls/${id}/refresh`, Recall, json({})),
  close: (id: string, close: RecallClose) => api(`/recalls/${id}/close`, Recall, json(close)),
  changeUnitStatus: (id: string, unitId: string, change: AffectedUnitStatusChange) => api(`/recalls/${id}/units/${unitId}/status`, Recall, json(change)),
  addUnitNote: (id: string, unitId: string, note: AffectedUnitNote) => api(`/recalls/${id}/units/${unitId}/notes`, Recall, json(note)),
};
